import { useRef, useState } from 'react';
import { Button, Col, Dropdown, Row, Spinner, Table } from 'react-bootstrap';
import Highlighter from 'react-highlight-words';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { useInfiniteQuery, useMutation } from 'react-query';
import { ReactComponent as ReactMoreIcon } from '../../../../assets/icons/more.svg';
import NotificationIcon from '../../../../constants/notificationIcon';
import { StyledPageTitle } from '../../../../styles/pageTitle';
import ConfirmationDialog from '../../../../utils/ConfirmationDialog';
import { StyledTh } from '../styles';
import ModulesCubage from '../../../../shared/interfaces/modules-cubage.interface';
import ModulesFilterSection from './FilterSection';
import useIntersectionObserver from '../../../../hooks/useIntersectionObserver';
import ModulesCubageModal from './ModulesModal';
import CubageHttpService, {
    ListCubageParams,
} from '../../../../services/http/cubage-http';
import ProductGroup from '../../../../constants/ProductGroup';
import handleResponseError from '../../../../utils/handleResponseError';

export interface ModulesFilterProps {
    filterTerm: string;
}

export default function ModulesCubageTable() {
    const rowsPerPage = 20;
    const [showAddModulesModal, setShowAddModulesModal] =
        useState<boolean>(false);
    const [showRemoveModulesModal, setShowRemoveModulesModal] =
        useState<boolean>(false);
    const [selectedModule, setSelectedModule] = useState<ModulesCubage | null>(
        null,
    );

    const form = useForm<ModulesFilterProps>({
        shouldUnregister: false,
        defaultValues: {
            filterTerm: '',
        },
    });

    const formData = form.watch();
    const termTrimmed = formData.filterTerm.trim();

    const ModulesQuery = useInfiniteQuery({
        queryKey: ['modules-cubage', termTrimmed],
        staleTime: 300000,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        queryFn: async ({ pageParam = 0 }) => {
            const params: ListCubageParams = {
                skip: pageParam,
                take: rowsPerPage,
                term: termTrimmed || '',
            };

            const response = await CubageHttpService.readMany(
                ProductGroup.ModulesCubage,
                params,
            );

            return {
                data: response.data.results,
                currentPage: pageParam,
                pages: Math.ceil(response.data.metadata.total / rowsPerPage),
            };
        },
        getNextPageParam: ({ currentPage, pages }) =>
            currentPage + 1 < pages ? currentPage + 1 : null,
        onError: (err) => {
            handleResponseError(err, 'Erro ao buscar módulos');
        },
    });

    const target = useRef(null);

    useIntersectionObserver({
        target,
        onIntersect: ModulesQuery.fetchNextPage,
        enabled: ModulesQuery.hasNextPage,
    });

    const showLoading =
        ModulesQuery.isLoading ||
        ModulesQuery.isFetchingNextPage ||
        !ModulesQuery.isFetched;

    const deleteMutation = useMutation(
        async () => {
            if (!selectedModule?.id) {
                throw new Error('Nenhum módulo selecionado para exclusão');
            }

            const response = await CubageHttpService.delete(
                ProductGroup.ModulesCubage,
                selectedModule?.id,
            );

            return response.data;
        },
        {
            onError: (error: any) => {
                handleResponseError(error, 'Erro ao excluir módulo');
            },
            onSuccess: async () => {
                toast.success('Módulo removido com sucesso');

                ModulesQuery.refetch();
            },
        },
    );

    const ModuleCubages = ModulesQuery.data?.pages.reduce(
        (accumulator, current) => accumulator.concat(current.data),
        [] as ModulesCubage[],
    );

    return (
        <>
            <Row>
                <Col>
                    <StyledPageTitle className="mt-2">
                        Cubagem dos módulos
                    </StyledPageTitle>
                </Col>
                <Button
                    className="float-right w-20 m-2"
                    style={{ width: '180px' }}
                    onClick={() => {
                        setShowAddModulesModal(true);
                    }}
                >
                    Nova configuração
                </Button>
            </Row>
            <ModulesFilterSection control={form.control} />
            <Row>
                <Table bordered hover size="sm" className="text-center">
                    <thead>
                        <tr>
                            <StyledTh>ID</StyledTh>
                            <StyledTh>Código ERP</StyledTh>
                            <StyledTh>Quantidade módulos para cubagem</StyledTh>
                            <StyledTh>Cubagem</StyledTh>
                            <StyledTh />
                        </tr>
                    </thead>
                    <tbody>
                        {ModuleCubages &&
                            ModuleCubages.map((ModuleCubage: ModulesCubage) => (
                                <tr key={ModuleCubage.id}>
                                    <td>
                                        <Highlighter
                                            autoEscape
                                            highlightClassName="highlight-term"
                                            searchWords={[termTrimmed]}
                                            textToHighlight={ModuleCubage?.id.toString()}
                                        />
                                    </td>
                                    <td>
                                        <Highlighter
                                            autoEscape
                                            highlightClassName="highlight-term"
                                            searchWords={[termTrimmed]}
                                            textToHighlight={ModuleCubage.erpProductCode.toString()}
                                        />
                                    </td>
                                    <td>
                                        <Highlighter
                                            autoEscape
                                            highlightClassName="highlight-term"
                                            searchWords={[termTrimmed]}
                                            textToHighlight={ModuleCubage.modulesForCubage.toString()}
                                        />
                                    </td>
                                    <td>
                                        <Highlighter
                                            autoEscape
                                            highlightClassName="highlight-term"
                                            searchWords={[termTrimmed]}
                                            textToHighlight={ModuleCubage.cubage.toString()}
                                        />
                                    </td>
                                    <td>
                                        <Dropdown key="left">
                                            <Dropdown.Toggle
                                                bsPrefix="nexen"
                                                className="mt-n3 mb-n3"
                                                as={Button}
                                                variant="text"
                                            >
                                                <ReactMoreIcon
                                                    fill="#bdbdbd"
                                                    width="10"
                                                    height="20"
                                                />
                                            </Dropdown.Toggle>

                                            <Dropdown.Menu>
                                                <Dropdown.Item
                                                    onClick={() => {
                                                        setSelectedModule(
                                                            ModuleCubage,
                                                        );

                                                        setShowAddModulesModal(
                                                            true,
                                                        );
                                                    }}
                                                >
                                                    Editar
                                                </Dropdown.Item>
                                                <Dropdown.Item
                                                    onClick={() => {
                                                        setSelectedModule(
                                                            ModuleCubage,
                                                        );

                                                        setShowRemoveModulesModal(
                                                            true,
                                                        );
                                                    }}
                                                >
                                                    Remover
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </Table>
            </Row>
            <Row className="mx-4" ref={target}>
                <br />
                {showLoading && (
                    <Col md={12} className="text-center">
                        <Spinner animation="border" />
                    </Col>
                )}

                {ModuleCubages?.length === 0 && (
                    <Col md={12} className="text-center">
                        <strong style={{ color: '#adadad' }}>
                            Sem itens para carregar
                        </strong>
                    </Col>
                )}
            </Row>

            {showAddModulesModal && (
                <ModulesCubageModal
                    selectedModule={selectedModule}
                    show={showAddModulesModal}
                    refetch={ModulesQuery.refetch}
                    onHide={() => {
                        setSelectedModule(null);
                        setShowAddModulesModal(false);
                    }}
                />
            )}
            {showRemoveModulesModal && (
                <ConfirmationDialog
                    show={showRemoveModulesModal}
                    onHide={() => {
                        setShowRemoveModulesModal(false);
                        setSelectedModule(null);
                        ModulesQuery.refetch();
                    }}
                    onConfirm={async () => deleteMutation.mutateAsync()}
                    icon={NotificationIcon.Warning}
                    title="Excluir Configuração de Cubagem"
                    text="Deseja excluir a configuração de cubagem selecionada?"
                />
            )}
        </>
    );
}
